import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import * as d3 from 'd3';

import { MagnetDistanceUserData } from '@app/models/magnet-distance';

const width = 280;
const height = 280;
const outerRadius = Math.min(width, height) / 2;
const innerRadius = outerRadius / 1.5;

@Component({
  selector: 'app-distance-circle',
  template: '',
})
export class DistanceCircleComponent implements OnChanges {
  private elementRef = inject(ElementRef);

  @Input() distances!: MagnetDistanceUserData[];
  @Input() position!: number;
  @Input() resolution!: number;

  @Input() textColor = '#25343b';

  @Output() sliceMouseenter = new EventEmitter<any>();
  @Output() sliceMouseleave = new EventEmitter<any>();

  svg!: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  g!: d3.Selection<SVGGElement, unknown, null, undefined>;
  positionText!: d3.Selection<SVGTextElement, unknown, null, undefined>;
  avgDistanceText!: d3.Selection<SVGTextElement, unknown, null, undefined>;
  avgDistanceLabelText: d3.Selection<SVGTextElement, unknown, null, undefined>;
  positionLine: d3.Selection<SVGLineElement, unknown, null, undefined>;

  private pieFn = d3.pie<DistanceCircleComponent, MagnetDistanceUserData>().value(() => 1);

  ngOnChanges(changes: SimpleChanges): void {
    const { distances, position, resolution } = changes;

    if (distances) {
      if (distances.isFirstChange()) {
        this.create();
      } else if (distances.currentValue.length !== distances.previousValue.length) {
        this.svg.remove();
        this.create();
      } else {
        this.update();
      }
    }

    if (position || resolution) {
      this.rotate();
    }
  }

  create(): void {
    this.svg = d3
      .select(this.elementRef.nativeElement)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', '0 0 280 280')
      .attr('preserveAspectRatio', 'none');

    this.g = this.svg.append('g').attr('transform', `translate(${width / 2} ${height / 2})`);

    const dataReady = this.pieFn(this.distances);

    const path = d3.arc().innerRadius(innerRadius).outerRadius(outerRadius);

    this.g
      .selectAll('path')
      .data(dataReady)
      .enter()
      .append('path')
      .attr('d', path as any)
      .attr('fill', (d) => d.data.color)
      .attr('stroke', 'white')
      .style('stroke-width', '1')
      .style('stroke-opacity', '0.25')
      .on('mouseenter', (event, payload) => this.sliceMouseenter.emit({ event, payload }))
      .on('mouseleave', (event, payload) => this.sliceMouseleave.emit({ event, payload }));

    this.svg
      .append('circle')
      .attr('r', innerRadius)
      .attr('cx', width / 2)
      .attr('cy', height / 2)
      .style('fill', 'white')
      .style('stroke', 'rgba(35, 31, 32, 0.1)')
      .style('stroke-width', 8);

    this.positionLine = this.svg
      .append('line')
      .attr('x1', width / 2)
      .attr('y1', outerRadius - innerRadius + 7)
      .attr('x2', width / 2)
      .attr('y2', outerRadius - innerRadius + 22)
      .attr('transform-origin', 'center')
      .style('stroke-width', 3)
      .style('stroke', this.textColor)
      .style('fill', 'none');

    const textContainer = this.svg
      .append('g')
      .attr('transform', `translate(${width / 2} ${height / 2})`)
      .attr('fill', 'lightblue');

    this.positionText = textContainer
      .append('text')
      .text(this.position)
      .attr('transform', 'translate(0 8)')
      .attr('text-anchor', 'middle')
      .attr('font-family', 'Roboto, sans-serif')
      .attr('font-weight', '300')
      .attr('font-size', '24')
      .attr('fill', this.textColor);

    // this.avgDistanceText = textContainer.append('text')
    //   .attr('transform', 'translate(0 -48)')
    //   .attr('text-anchor', 'middle')
    //   .attr('font-family', 'Roboto')
    //   .attr('font-size', '16')
    //   .attr('fill', this.textColor);

    // this.avgDistanceLabelText = textContainer.append('text')
    //   .attr('transform', 'translate(0 -32)')
    //   .attr('text-anchor', 'middle')
    //   .attr('font-family', 'Roboto')
    //   .attr('font-size', '14')
    //   .attr('fill', '#a8a8a8');
  }

  rotate(): void {
    this.positionText.text(this.position);

    const t = this.position % this.resolution;
    const v = this.resolution / 360;
    const degrees = Math.round(t / v);

    this.positionLine.attr('transform', `rotate(${degrees})`);

    this.g.attr('transform', `translate(${width / 2} ${height / 2}) rotate(${degrees} 0 0)`);

    // const knownDistances = this.distances.filter((d) => d.distance > 0);
    // const sum = knownDistances.reduce((p, d) => p + d.distance, 0);
    // const avg = sum / knownDistances.length;

    // this.avgDistanceText.text(avg ? `${avg.toFixed(1)} mm` : '');
    // this.avgDistanceLabelText.text(avg ? 'average distance' : '');
  }

  update(): void {
    const dataReady = this.pieFn(this.distances);
    this.g
      .selectAll('path')
      .data(dataReady)
      .transition()
      .duration(500)
      .attr('fill', (d) => d.data.color);
  }
}
